import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * One-off bulk invite tool — upload a CSV of Name,Email and send an invitation
 * to each address using the same flow as "Invite Existing Customer".
 */
import { useRef, useState } from 'react';
import { addHours } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import AdminLayout from '@/components/AdminLayout';
function parseCSV(text) {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    const results = [];
    for (const line of lines) {
        // Simple CSV split — handles quoted fields
        const cols = line.match(/(".*?"|[^,]+)/g)?.map((c) => c.replace(/^"|"$/g, '').trim()) ?? [];
        if (cols.length < 2)
            continue;
        // Detect header row
        const maybeEmail = cols[1].toLowerCase();
        if (maybeEmail === 'email' || maybeEmail === 'e-mail')
            continue;
        const name = cols[0];
        const email = cols[1];
        if (!email.includes('@'))
            continue;
        results.push({ name, email });
    }
    return results;
}
export default function BulkInvite() {
    const { user } = useAuth();
    const fileRef = useRef(null);
    const [rows, setRows] = useState([]);
    const [running, setRunning] = useState(false);
    const [done, setDone] = useState(false);
    const [invitationExpiryHours, setInvitationExpiryHours] = useState(72);
    useState(() => {
        supabase.from('settings').select('value').eq('key', 'invitation_expiry_hours').single()
            .then(({ data }) => { if (data?.value)
            setInvitationExpiryHours(Number(data.value)); });
    });
    function handleFile(e) {
        const file = e.target.files?.[0];
        if (!file)
            return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const text = ev.target?.result;
            const parsed = parseCSV(text);
            setRows(parsed.map((r) => ({ ...r, status: 'pending' })));
            setDone(false);
        };
        reader.readAsText(file);
    }
    function updateRow(index, patch) {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    }
    async function sendInvite(row, index) {
        updateRow(index, { status: 'sending', note: undefined });
        const email = row.email.toLowerCase().trim();
        // Skip if account already exists
        const { count: profileCount } = await supabase
            .from('profiles')
            .select('id', { count: 'exact', head: true })
            .eq('email', email);
        if (profileCount && profileCount > 0) {
            updateRow(index, { status: 'skipped', note: 'Account already exists' });
            return;
        }
        // Skip if a pending invitation already exists
        const { count: invCount } = await supabase
            .from('invitations')
            .select('id', { count: 'exact', head: true })
            .eq('email', email)
            .eq('status', 'pending');
        if (invCount && invCount > 0) {
            updateRow(index, { status: 'skipped', note: 'Invitation already pending' });
            return;
        }
        const token = crypto.randomUUID();
        const { data: inviteRow, error: insertErr } = await supabase
            .from('invitations')
            .insert({
            email,
            token,
            invited_by: user.id,
            expires_at: addHours(new Date(), invitationExpiryHours).toISOString(),
            status: 'pending',
        })
            .select('id')
            .single();
        if (insertErr || !inviteRow) {
            updateRow(index, { status: 'error', note: insertErr?.message ?? 'Insert failed' });
            return;
        }
        const { error: fnErr } = await supabase.functions.invoke('send-invitation', {
            body: { invitationId: inviteRow.id, token, email },
        });
        if (fnErr) {
            updateRow(index, { status: 'error', note: 'Invitation created but email failed' });
        }
        else {
            updateRow(index, { status: 'sent' });
        }
    }
    async function handleRun() {
        if (!user || running)
            return;
        setRunning(true);
        for (let i = 0; i < rows.length; i++) {
            if (rows[i].status !== 'pending')
                continue;
            await sendInvite(rows[i], i);
            // Small pause to avoid hammering the edge function
            await new Promise((r) => setTimeout(r, 300));
        }
        setRunning(false);
        setDone(true);
    }
    const counts = {
        sent: rows.filter((r) => r.status === 'sent').length,
        skipped: rows.filter((r) => r.status === 'skipped').length,
        error: rows.filter((r) => r.status === 'error').length,
        pending: rows.filter((r) => r.status === 'pending').length,
    };
    const statusBadge = (s) => {
        const map = {
            pending: 'bg-gray-100 text-gray-500',
            sending: 'bg-blue-100 text-blue-600',
            sent: 'bg-green-100 text-green-700',
            skipped: 'bg-amber-100 text-amber-700',
            error: 'bg-red-100 text-red-700',
        };
        return map[s];
    };
    return (_jsx(AdminLayout, { children: _jsxs("main", { className: "max-w-4xl mx-auto px-4 py-8", children: [_jsx("h1", { className: "text-2xl font-bold text-gray-900 mb-1", children: "Bulk Invite" }), _jsxs("p", { className: "text-sm text-gray-500 mb-6", children: ["Upload a CSV with ", _jsx("strong", { children: "Name" }), " and ", _jsx("strong", { children: "Email" }), " columns to send invitations in bulk. Same flow as \"Invite Existing Customer\" \u2014 skips addresses that already have an account or a pending invite."] }), _jsxs("div", { className: "bg-white border border-gray-200 rounded-xl p-5 mb-6", children: [_jsx("label", { className: "block text-sm font-semibold text-gray-700 mb-2", children: "CSV file" }), _jsx("input", { ref: fileRef, type: "file", accept: ".csv,text/csv", onChange: handleFile, className: "text-sm text-gray-600" }), _jsx("p", { className: "text-xs text-gray-400 mt-2", children: "Expected columns: Name, Email (header row optional)" })] }), rows.length > 0 && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex flex-wrap gap-4 mb-4 text-sm", children: [_jsxs("span", { className: "font-medium text-gray-700", children: [rows.length, " rows loaded"] }), done && (_jsxs(_Fragment, { children: [_jsxs("span", { className: "text-green-600", children: [counts.sent, " sent"] }), _jsxs("span", { className: "text-amber-600", children: [counts.skipped, " skipped"] }), counts.error > 0 && _jsxs("span", { className: "text-red-600", children: [counts.error, " errors"] })] })), running && (_jsxs("span", { className: "text-blue-600", children: ["Sending\u2026 (", rows.filter((r) => r.status === 'sent' || r.status === 'skipped' || r.status === 'error').length, " / ", rows.length, ")"] }))] }), !done && (_jsx("button", { onClick: handleRun, disabled: running || counts.pending === 0, className: "mb-6 px-5 py-2.5 bg-brand-green text-white text-sm font-medium rounded-lg hover:bg-brand-green/90 disabled:opacity-50 disabled:cursor-not-allowed", children: running ? 'Sending…' : `Send ${counts.pending} invitation${counts.pending !== 1 ? 's' : ''}` })), _jsx("div", { className: "bg-white border border-gray-200 rounded-xl overflow-hidden", children: _jsxs("table", { className: "w-full text-sm", children: [_jsx("thead", { children: _jsxs("tr", { className: "text-left text-xs text-gray-400 uppercase border-b border-gray-100", children: [_jsx("th", { className: "px-4 py-2 font-medium", children: "#" }), _jsx("th", { className: "px-4 py-2 font-medium", children: "Name" }), _jsx("th", { className: "px-4 py-2 font-medium", children: "Email" }), _jsx("th", { className: "px-4 py-2 font-medium", children: "Status" }), _jsx("th", { className: "px-4 py-2 font-medium", children: "Note" })] }) }), _jsx("tbody", { className: "divide-y divide-gray-50", children: rows.map((row, i) => (_jsxs("tr", { className: "hover:bg-gray-50", children: [_jsx("td", { className: "px-4 py-2 text-gray-400", children: i + 1 }), _jsx("td", { className: "px-4 py-2 text-gray-900", children: row.name }), _jsx("td", { className: "px-4 py-2 text-gray-600", children: row.email }), _jsx("td", { className: "px-4 py-2", children: _jsx("span", { className: `text-xs font-medium px-2 py-0.5 rounded-full capitalize ${statusBadge(row.status)}`, children: row.status }) }), _jsx("td", { className: "px-4 py-2 text-xs text-gray-400", children: row.note ?? '' })] }, i))) })] }) })] }))] }) }));
}
